/** Pre-battle briefing modal shown before the coin-flip ceremony for
 *  campaign stages that have either a prose `introMessage`, a list of
 *  mission conditions, or both. The intent is to give the player a
 *  chance to read the historical scene-setting *and* any unusual
 *  starting conditions (a pre-placed boss unit on the ring, a reduced
 *  HP pool, an unusual starting clock) before committing to the match —
 *  playtesters were caught off guard by Pharsalus' opening Cataphract
 *  and didn't realize the stage description was meant to set
 *  expectations.
 *
 *  Unlike the coin-flip overlay, this modal does NOT auto-dismiss — the
 *  player must explicitly choose. OK proceeds to the match; Cancel (or
 *  Esc) backs out, returning to whatever screen was underneath. The
 *  caller is responsible for not creating any game state until
 *  `onConfirm` fires, so a Cancel is a true no-op. */
export function showStageIntro(
  stageName: string,
  message: string | null,
  conditions: string[],
  onConfirm: () => void,
  onCancel: () => void,
): void {
  const overlay = document.createElement('div');
  overlay.className = 'stage-intro-overlay';
  // Prose paragraph — only rendered when the stage actually has one.
  // Stages with conditions but no prose still show this modal; the
  // header + condition list are enough on their own.
  const messageBlock = message
    ? `<p class="stage-intro-message">${escapeHtml(message)}</p>`
    : '';
  // Hand-authored condition list — separated from the prose by extra
  // top spacing so the "Mission Conditions" heading reads as its own
  // section rather than continuing the paragraph.
  const conditionsBlock = conditions.length > 0
    ? `<div class="stage-intro-conditions">
         <div class="stage-intro-conditions-header">Mission Conditions</div>
         <ul class="stage-intro-conditions-list">
           ${conditions.map(c => `<li>${escapeHtml(c)}</li>`).join('')}
         </ul>
       </div>`
    : '';
  overlay.innerHTML = `
    <div class="stage-intro-panel">
      <h2 class="stage-intro-title">${escapeHtml(stageName)}</h2>
      ${messageBlock}
      ${conditionsBlock}
      <div class="stage-intro-buttons">
        <button class="action-button secondary stage-intro-cancel">Cancel</button>
        <button class="action-button stage-intro-ok">OK</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const dismiss = (cb: () => void): void => {
    overlay.classList.add('fade-out');
    document.removeEventListener('keydown', onKey);
    setTimeout(() => { overlay.remove(); cb(); }, 200);
  };

  const okBtn = overlay.querySelector('.stage-intro-ok') as HTMLButtonElement;
  const cancelBtn = overlay.querySelector('.stage-intro-cancel') as HTMLButtonElement;
  okBtn.addEventListener('click', () => dismiss(onConfirm));
  cancelBtn.addEventListener('click', () => dismiss(onCancel));
  // Esc cancels — mirrors the card-detail modal's convention. Don't
  // bind Enter to OK: card-detail and other overlays use Enter for
  // their own controls, and a global Enter binding here could fire
  // through to a still-focused button on the underlying screen.
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') dismiss(onCancel);
  };
  document.addEventListener('keydown', onKey);
  // Focus OK so Space / Enter on a desktop keyboard confirms without a
  // mouse — the default expected action.
  setTimeout(() => okBtn.focus(), 0);
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
